/* ==========================================================================
   eg-limphoria — landing page behaviour
   Tier selection, subscription toggle, accordions, carousels, gallery,
   dynamic ship date, sticky add-to-cart and direct-to-checkout ATC.
   ========================================================================== */
(function () {
  'use strict';

  var SHIP_OFFSET_DAYS = 4;

  function init(root) {
    if (!root || root.dataset.egInit === 'true') return;
    root.dataset.egInit = 'true';

    var subscribeBox = root.querySelector('[data-eg-subscribe]');

    var state = {
      tier: root.querySelector('[data-eg-tier].is-selected') || root.querySelector('[data-eg-tier]'),
      // the markup owns the default, and a reloaded page can restore its own
      subscribe: !!(subscribeBox && subscribeBox.checked)
    };

    countdown(root);
    anchors(root);
    gallery(root);
    accordions(root);
    carousels(root);
    shipDate(root);
    purchase(root, state);
    stickyBar(root);
  }

  /* ---------------------------------------------------------------- utils */

  function on(el, evt, fn) {
    if (el) el.addEventListener(evt, fn);
  }

  function each(list, fn) {
    Array.prototype.forEach.call(list, fn);
  }

  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  /* ------------------------------------------------------------ countdown */

  function countdown(root) {
    var el = root.querySelector('[data-eg-countdown]');
    if (!el) return;

    var TOTAL = 60 * 60 * 1000; // one hour
    var key = 'egLimphoriaSaleEnd';
    var end = parseInt(sessionStorage.getItem(key) || '0', 10);

    if (!end || end < Date.now()) {
      end = Date.now() + TOTAL;
      try {
        sessionStorage.setItem(key, String(end));
      } catch (e) {
        /* private mode — keep the in-memory value */
      }
    }

    function tick() {
      var left = Math.max(0, end - Date.now());
      var s = Math.floor(left / 1000);
      el.textContent = pad(Math.floor(s / 3600)) + ':' + pad(Math.floor((s % 3600) / 60)) + ':' + pad(s % 60);
      if (left <= 0) {
        end = Date.now() + TOTAL;
        try {
          sessionStorage.setItem(key, String(end));
        } catch (e) {
          /* noop */
        }
      }
    }

    tick();
    setInterval(tick, 1000);
  }

  /* -------------------------------------------------------- anchor scroll */

  function anchors(root) {
    each(root.querySelectorAll('[data-eg-scroll]'), function (link) {
      on(link, 'click', function (event) {
        var target = root.querySelector(link.getAttribute('href'));
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  /* -------------------------------------------------------------- gallery */

  function gallery(root) {
    var track = root.querySelector('[data-eg-gallery-track]');
    var slides = track ? track.querySelectorAll('[data-eg-gallery-slide]') : [];
    var thumbs = root.querySelectorAll('[data-eg-thumb]');
    var strip = root.querySelector('[data-eg-thumbs]');
    if (!track || !slides.length || !thumbs.length) return;

    // the thumbnails are navigation only — they scroll the matching slide into view
    each(thumbs, function (thumb, i) {
      on(thumb, 'click', function () {
        if (!slides[i]) return;
        track.scrollTo({ left: slides[i].offsetLeft - slides[0].offsetLeft, behavior: 'smooth' });
      });
    });

    function sync() {
      var width = slides[0].getBoundingClientRect().width;
      var index = width > 0 ? Math.round(track.scrollLeft / width) : 0;
      index = Math.max(0, Math.min(index, thumbs.length - 1));

      each(thumbs, function (thumb, i) {
        thumb.classList.toggle('is-active', i === index);
      });

      // keep the active thumb inside the (scrollable) strip
      if (strip && strip.scrollWidth > strip.clientWidth) {
        var t = thumbs[index];
        var left = t.offsetLeft - strip.offsetLeft;
        if (left < strip.scrollLeft || left + t.offsetWidth > strip.scrollLeft + strip.clientWidth) {
          strip.scrollTo({ left: left - (strip.clientWidth - t.offsetWidth) / 2, behavior: 'smooth' });
        }
      }
    }

    on(track, 'scroll', sync);
    window.addEventListener('resize', sync);
    sync();
  }

  /* ----------------------------------------------------------- accordions */

  function accordions(root) {
    var items = root.querySelectorAll('[data-eg-acc]');

    function measure(item) {
      var panel = item.querySelector('[data-eg-acc-panel]');
      if (panel) item.style.setProperty('--eg-panel-h', panel.scrollHeight + 'px');
    }

    function setOpen(item, open) {
      item.classList.toggle('is-open', open);
      var toggle = item.querySelector('[data-eg-acc-toggle]');
      if (toggle) toggle.setAttribute('aria-expanded', String(open));
      if (open) measure(item);
    }

    each(items, function (item) {
      var toggle = item.querySelector('[data-eg-acc-toggle]');
      if (!toggle) return;

      measure(item);

      on(toggle, 'click', function () {
        var willOpen = !item.classList.contains('is-open');
        var group = item.closest('[data-eg-acc-group]');

        if (group && willOpen) {
          each(group.querySelectorAll('[data-eg-acc]'), function (sibling) {
            if (sibling !== item) setOpen(sibling, false);
          });
        }

        setOpen(item, willOpen);
      });
    });

    // on mobile the whole card is the hit area, not just the +/- button
    each(root.querySelectorAll('[data-eg-acc-surface]'), function (surface) {
      on(surface, 'click', function (event) {
        var toggle = surface.querySelector('[data-eg-acc-toggle]');
        if (!toggle || toggle.offsetParent === null) return; // button hidden = desktop
        if (toggle.contains(event.target)) return; // its own handler will fire
        toggle.click();
      });
    });

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        each(items, measure);
      }, 150);
    });
  }

  /* ------------------------------------------------------------ carousels */

  /*
   * Scroll-snap carousel following the SliderComponent model used in our other
   * themes: the dot count is the number of scroll *positions*, not slides —
   * slides already on screen don't get their own dot.
   */
  function carousels(root) {
    each(root.querySelectorAll('[data-eg-carousel]'), function (carousel) {
      var track = carousel.querySelector('[data-eg-track]');
      var slides = track ? track.querySelectorAll('[data-eg-slide]') : [];
      var dotsHost = carousel.querySelector('[data-eg-dots]');
      var prev = carousel.querySelector('[data-eg-prev]');
      var next = carousel.querySelector('[data-eg-next]');
      if (!track || !slides.length) return;

      var current = 1;
      var pages = 1;
      var renderedDots = 0;

      function step() {
        var width = slides[0].getBoundingClientRect().width;
        var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        return { width: width, gap: gap, size: width + gap };
      }

      function scrollToPage(page) {
        var index = Math.max(0, Math.min(page, pages) - 1);
        track.scrollTo({ left: slides[index].offsetLeft - slides[0].offsetLeft, behavior: 'smooth' });
      }

      function buildDots(count) {
        dotsHost.innerHTML = '';
        for (var i = 1; i <= count; i++) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'eg-limphoria__dot';
          dot.setAttribute('aria-label', 'Go to slide ' + i);
          dot.dataset.page = String(i);
          dotsHost.appendChild(dot);
        }
        renderedDots = count;
      }

      function sync() {
        var m = step();
        if (m.size <= 0) return;

        var perView = Math.max(1, Math.floor((track.clientWidth + m.gap) / m.size));
        pages = Math.max(1, slides.length - (perView - 1));
        current = Math.max(1, Math.min(pages, Math.round(track.scrollLeft / m.size) + 1));

        if (dotsHost) {
          if (renderedDots !== pages) buildDots(pages);
          each(dotsHost.children, function (dot, i) {
            dot.classList.toggle('is-active', i === current - 1);
          });
          dotsHost.hidden = pages < 2;
        }
        if (prev) prev.disabled = current <= 1;
        if (next) next.disabled = current >= pages;
      }

      if (dotsHost) {
        on(dotsHost, 'click', function (event) {
          var dot = event.target.closest('[data-page]');
          if (dot) scrollToPage(Number(dot.dataset.page));
        });
      }

      on(prev, 'click', function () {
        scrollToPage(current - 1);
      });
      on(next, 'click', function () {
        scrollToPage(current + 1);
      });

      on(track, 'scroll', sync);
      window.addEventListener('resize', sync);
      sync();
    });
  }

  /* ------------------------------------------------------------ ship date */

  function shipDate(root) {
    var el = root.querySelector('[data-eg-ship-date]');
    if (!el) return;

    var d = new Date();
    d.setDate(d.getDate() + SHIP_OFFSET_DAYS);

    var months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    var day = d.getDate();
    var suffix = 'th';
    if (day % 10 === 1 && day !== 11) suffix = 'st';
    else if (day % 10 === 2 && day !== 12) suffix = 'nd';
    else if (day % 10 === 3 && day !== 13) suffix = 'rd';

    el.textContent = months[d.getMonth()] + ' ' + day + suffix;
  }

  /* ------------------------------------------- tiers / purchase / add-to-cart */

  function purchase(root, state) {
    var tiers = root.querySelectorAll('[data-eg-tier]');
    var subscribe = root.querySelector('[data-eg-subscribe]');
    var frequency = root.querySelector('[data-eg-frequency]');
    var atcPrice = root.querySelector('[data-eg-atc-price]');
    var atcCompare = root.querySelector('[data-eg-atc-compare]');

    function render() {
      var tier = state.tier;
      if (!tier) return;

      var sub = state.subscribe;

      each(tiers, function (t) {
        var selected = t === tier;
        t.classList.toggle('is-selected', selected);
        t.setAttribute('aria-checked', String(selected));

        var price = t.querySelector('[data-eg-tier-price]');
        var unit = t.querySelector('[data-eg-tier-unit]');
        if (price) price.textContent = sub ? t.dataset.priceSub : t.dataset.priceOnce;
        // the per-bottle line follows the pack price, subscription discount included
        if (unit) unit.textContent = sub ? t.dataset.unitSub : t.dataset.unitOnce;
      });

      var headline = sub ? tier.dataset.priceSub : tier.dataset.priceOnce;
      if (atcPrice) atcPrice.textContent = headline;
      if (atcCompare) atcCompare.textContent = tier.dataset.compare || '';

      // each pack size refills on its own cadence (4 / 8 / 12 weeks)
      if (frequency && tier.dataset.frequency) frequency.textContent = tier.dataset.frequency;
    }

    each(tiers, function (tier) {
      on(tier, 'click', function () {
        state.tier = tier;
        render();
      });
    });

    if (subscribe) {
      on(subscribe, 'change', function () {
        state.subscribe = subscribe.checked;
        render();
      });
    }

    render();
    addToCart(root, state);
  }

  function addToCart(root, state) {
    var buttons = root.querySelectorAll('[data-eg-atc]');
    var errors = root.querySelectorAll('[data-eg-cart-error]');

    function showError(message) {
      each(errors, function (el) {
        el.textContent = message;
        el.hidden = !message;
      });
    }
    var addUrl = root.dataset.cartAddUrl || '/cart/add.js';
    var clearUrl = root.dataset.cartClearUrl || '/cart/clear.js';

    function postJSON(url, body) {
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body || {})
      }).then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) throw new Error(data.description || data.message || 'Cart request failed');
          return data;
        });
      });
    }

    each(buttons, function (button) {
      on(button, 'click', function () {
        var tier = state.tier;
        if (!tier) return;

        var body = {
          items: [{ id: Number(tier.dataset.variantId), quantity: 1 }]
        };
        if (state.subscribe && tier.dataset.sellingPlan) {
          body.items[0].selling_plan = Number(tier.dataset.sellingPlan);
        }

        showError('');
        each(buttons, function (b) {
          b.setAttribute('aria-busy', 'true');
        });

        // the offer is a single-item purchase, so anything already in the cart goes first
        postJSON(clearUrl)
          .then(function () {
            return postJSON(addUrl, body);
          })
          .then(function () {
            window.location.href = '/checkout';
          })
          .catch(function (err) {
            // stay on the page so the shopper can retry rather than bouncing them
            each(buttons, function (b) {
              b.removeAttribute('aria-busy');
            });
            showError(err.message || 'Something went wrong. Please try again.');
            console.error('[eg-limphoria] add to cart:', err);
          });
      });
    });
  }

  /* ------------------------------------------------------------ sticky bar */

  function stickyBar(root) {
    var sticky = root.querySelector('[data-eg-sticky]');
    var anchor = root.querySelector('.eg-limphoria__cta');
    if (!sticky || !anchor) return;

    sticky.removeAttribute('hidden');

    function update() {
      // shown as soon as the main add-to-cart has scrolled out of view
      sticky.classList.toggle('is-visible', anchor.getBoundingClientRect().bottom < 0);
    }

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  /* ----------------------------------------------------------------- boot */

  function boot() {
    each(document.querySelectorAll('[data-eg-limphoria]'), init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor re-renders
  document.addEventListener('shopify:section:load', boot);
})();
